import { MinusOutlined, PlusOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Drawer as AntdDrawer } from 'antd';
import classNames from 'classnames';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import React, { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Image as KonvaImage, Layer, Stage } from 'react-konva';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Box, useGetWarehouseBoxesQuery } from 'entities/Box';
import { useGetFloorsByWarehouseIdQuery } from 'entities/Floor';
import { RentOptionDocument } from 'entities/RentOption';
import { UserAccountType } from 'entities/User';
import { WarehouseForBooking } from 'entities/Warehouse';
import { getSelectedFilter } from 'features/BoxSizeFilter';
import { useFloorSchemaImage } from 'features/WarehouseMapConstructor';
import { getShapesBounds } from 'features/WarehouseMapConstructor/utils/helpers/getShapesBounds';
import { ReactComponent as CloseIcon } from 'shared/assets/icons/CloseIcon.svg';
import { Button } from 'shared/ui/Button';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { getBookingFloorMapData } from '../utils/helpers/getBookingFloorMapData';
import { getBookingMapCenteringSourceNode } from '../utils/helpers/getBookingMapCenteringSourceNode';
import { type BookingMapUnpickableTapKind } from '../utils/helpers/getBookingMapUnpickableTapKind';
import { showBoxNotification } from '../utils/helpers/showBookingMapUnpickableTapNotification';
import { useBookingMapStageState } from '../utils/hooks/useBookingMapStageState';
import { useSelectedFloor } from '../utils/hooks/useSelectedFloor';
import { useStagePanTween } from '../utils/hooks/useStagePanTween';
import { useStageTouchGestures } from '../utils/hooks/useStageTouchGestures';
import { BookingMapBoxPreviewCard } from './BookingMapBoxPreviewCard';
import { BookingMapDiscountBanner, BookingMapPriceBlock } from './BookingMapSelectedBoxPricing';
import { BookingMapShapes } from './BookingMapShapes';

interface WarehouseMapMobileProps {
  isOpen: boolean;
  onClose?: () => void;
  onSwitchToList?: () => void;
  selectedWarehouseId: string | undefined;
  selectedBoxId: string | undefined;
  rentOptions?: RentOptionDocument[];
  onSelectBox: (box: Box | undefined, sizeCodeId?: string) => void;
  onBookBox: (box: Box, sizeCodeId: string) => void;
  allowedSizeCodeId?: string;
  warehouseInfo?: Nullable<WarehouseForBooking>;
  accountType?: Nullable<UserAccountType>;
  isShowPromotionPriceNeeded?: boolean;
}

const MOBILE_MIN_ZOOM = 0.2;
const MOBILE_MAX_ZOOM = 3;
const MOBILE_ZOOM_STEP = 1.2;
const FIT_PADDING_PX = 32;
const MOBILE_BACKGROUND_MAX_OPACITY = 0.35;
const MAP_TOOLBAR_HEIGHT_PX = 52;

type LegendKey = 'free' | 'occupied' | 'selected' | 'different';

const LEGEND_COLOR_CLASSES: Record<LegendKey, string> = {
  free: 'border-[#2BA865] bg-[#E2FEF1]',
  occupied: 'border-[#EF4444] bg-[#FEE4E2]',
  selected: 'border-[#2058E7] bg-[#DEE8FF]',
  different: 'border-[#788593] bg-[#F3F6F9]',
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

export const WarehouseMapMobile: FC<WarehouseMapMobileProps> = memo((props) => {
  const {
    isOpen,
    onClose,
    onSwitchToList,
    selectedWarehouseId,
    selectedBoxId,
    rentOptions,
    onSelectBox: onSelectBoxProp,
    onBookBox,
    allowedSizeCodeId,
    warehouseInfo,
    accountType,
    isShowPromotionPriceNeeded = false,
  } = props;

  const { t } = useAppTranslation('booking');

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const stageRef = useRef<Konva.Stage | null>(null);
  /** shapesConfig of the floor we last auto-fit, so resizes don't reset the user's view. */
  const lastFittedKeyRef = useRef<string | null>(null);
  const stageParentRef = useRef<Nullable<HTMLDivElement>>(null);
  const resizeObserverRef = useRef<Nullable<ResizeObserver>>(null);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });

  const { stagePosition, stageScale, stagePositionRef, stageScaleRef, setStagePosition, setStageScale } = useBookingMapStageState();

  const setStageParentRef = useCallback((node: HTMLDivElement | null): void => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    stageParentRef.current = node;

    if (!node) {
      setCanvasDimensions({ width: 0, height: 0 });
      return;
    }

    const measure = (): void => {
      const width = node.offsetWidth || 0;
      const height = node.offsetHeight || 0;
      setCanvasDimensions((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));
    };

    measure();

    const ro = new ResizeObserver(() => {
      measure();
    });

    ro.observe(node);
    resizeObserverRef.current = ro;
  }, []);

  useEffect(
    () => (): void => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    },
    [],
  );
  const { image, loadImageSrc } = useFloorSchemaImage();

  const { data: floors } = useGetFloorsByWarehouseIdQuery(selectedWarehouseId, { skip: !selectedWarehouseId || !isOpen });
  const { data: boxes } = useGetWarehouseBoxesQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });

  const selectedFilter = useAppSelector(getSelectedFilter);

  const selectedBox = useMemo(() => boxes?.find((box) => box.boxId === selectedBoxId), [boxes, selectedBoxId]);
  const selectedBoxFloorId = useMemo(
    () => selectedBox?.floorId ?? selectedBox?.floor?.floorId,
    [selectedBox?.floor?.floorId, selectedBox?.floorId],
  );

  const { selectedFloor, setSelectedFloor } = useSelectedFloor({ floors, selectedBoxId, selectedBoxFloorId });

  const renderedFloor = useMemo(() => floors?.find((floor) => floor.floorId === selectedFloor), [floors, selectedFloor]);
  const floorSchemaImage = renderedFloor?.floorSchemaBackgroundUrl;

  const isSelectedBoxOccupied = Boolean(selectedBox?.contractNumber || selectedBox?.reservationNumber);
  const isSelectedBoxBookable = Boolean(selectedBox?.isActive && !isSelectedBoxOccupied);

  const { animateStagePositionTo, clearPanTween } = useStagePanTween({
    positionRef: stagePositionRef,
    setPosition: setStagePosition,
  });

  const handleBookBox = useCallback((): void => {
    if (!selectedBox || isSelectedBoxOccupied || !selectedBox.isActive) return;

    onBookBox(selectedBox, selectedBox.sizeCode.sizeCodeId);
  }, [isSelectedBoxOccupied, onBookBox, selectedBox]);

  const notifyBookingMapUnpickableTap = useCallback(
    (kind: BookingMapUnpickableTapKind): void => {
      showBoxNotification(kind, t);
    },
    [t],
  );

  const applyStageCursor = useCallback((cursor: string): void => {
    const container = stageRef.current?.container();

    if (container) {
      container.style.cursor = cursor;
    }
  }, []);

  const selectBox = useCallback(
    (box: Box, sizeCodeId: string): void => {
      onSelectBoxProp(box, sizeCodeId);
    },
    [onSelectBoxProp],
  );

  useEffect(() => {
    loadImageSrc(floorSchemaImage ?? '');
  }, [floorSchemaImage, loadImageSrc]);

  const { shapes, imageTransform, labelFontSize } = useMemo(
    () => getBookingFloorMapData({ shapesConfig: renderedFloor?.shapesConfig, mapConfig: renderedFloor?.mapConfig }),
    [renderedFloor?.mapConfig, renderedFloor?.shapesConfig],
  );
  const hasFloorMap = shapes.length > 0;

  const shapesConfigKey = renderedFloor?.shapesConfig ?? '';

  const stageWidth = Math.max(canvasDimensions.width, 1);
  const stageHeight = Math.max(canvasDimensions.height, 1);

  const zoomAtCenter = useCallback(
    (nextScaleRaw: number): void => {
      clearPanTween();

      const oldScale = stageScaleRef.current;
      const nextScale = clamp(nextScaleRaw, MOBILE_MIN_ZOOM, MOBILE_MAX_ZOOM);

      if (nextScale === oldScale) {
        return;
      }

      const pointer = { x: stageWidth / 2, y: stageHeight / 2 };
      const pos = stagePositionRef.current;
      const worldPoint = {
        x: (pointer.x - pos.x) / oldScale,
        y: (pointer.y - pos.y) / oldScale,
      };
      const newPos = {
        x: pointer.x - worldPoint.x * nextScale,
        y: pointer.y - worldPoint.y * nextScale,
      };

      stageScaleRef.current = nextScale;
      stagePositionRef.current = newPos;
      setStageScale(nextScale);
      setStagePosition(newPos);
    },
    [clearPanTween, setStagePosition, setStageScale, stageHeight, stagePositionRef, stageScaleRef, stageWidth],
  );

  const zoomIn = useCallback((): void => {
    zoomAtCenter(stageScaleRef.current * MOBILE_ZOOM_STEP);
  }, [stageScaleRef, zoomAtCenter]);

  const zoomOut = useCallback((): void => {
    zoomAtCenter(stageScaleRef.current / MOBILE_ZOOM_STEP);
  }, [stageScaleRef, zoomAtCenter]);

  const canZoomIn = useMemo(() => clamp(stageScale * MOBILE_ZOOM_STEP, MOBILE_MIN_ZOOM, MOBILE_MAX_ZOOM) > stageScale, [stageScale]);
  const canZoomOut = useMemo(() => clamp(stageScale / MOBILE_ZOOM_STEP, MOBILE_MIN_ZOOM, MOBILE_MAX_ZOOM) < stageScale, [stageScale]);

  const mobileBackgroundOpacity = useMemo(
    () => Math.min(imageTransform.opacity ?? 1, MOBILE_BACKGROUND_MAX_OPACITY),
    [imageTransform.opacity],
  );

  const legendItems = useMemo(
    (): Array<{ key: LegendKey; label: string }> => [
      { key: 'free', label: t('Free boxes') },
      { key: 'occupied', label: t('Occupied boxes') },
      { key: 'selected', label: t('Selected boxes') },
      { key: 'different', label: t('Different size box') },
    ],
    [t],
  );

  const mapToolbarBottom = selectedBox
    ? 'calc(env(safe-area-inset-bottom, 0px) + 12px)'
    : `calc(env(safe-area-inset-bottom, 0px) + ${MAP_TOOLBAR_HEIGHT_PX}px)`;

  /**
   * Fits the active floor's shapes into the stage on first render. Re-runs when the floor
   * changes so each switch shows the whole floor instead of a clipped corner.
   */
  const fitToContent = useCallback((): void => {
    if (stageWidth <= 1 || stageHeight <= 1) {
      return;
    }

    const bounds = getShapesBounds(shapes);

    if (!bounds || bounds.width === 0 || bounds.height === 0) {
      setStageScale(0.6);
      setStagePosition({ x: 0, y: 0 });
      stageScaleRef.current = 0.6;
      stagePositionRef.current = { x: 0, y: 0 };

      return;
    }

    const availableWidth = Math.max(1, stageWidth - FIT_PADDING_PX * 2);
    const availableHeight = Math.max(1, stageHeight - FIT_PADDING_PX * 2);
    const scaleX = availableWidth / bounds.width;
    const scaleY = availableHeight / bounds.height;
    const fitScale = clamp(Math.min(scaleX, scaleY), MOBILE_MIN_ZOOM, MOBILE_MAX_ZOOM);

    const boundsCenter = {
      x: bounds.minX + bounds.width / 2,
      y: bounds.minY + bounds.height / 2,
    };
    const nextPos = {
      x: stageWidth / 2 - boundsCenter.x * fitScale,
      y: stageHeight / 2 - boundsCenter.y * fitScale,
    };

    stageScaleRef.current = fitScale;
    stagePositionRef.current = nextPos;
    setStageScale(fitScale);
    setStagePosition(nextPos);
  }, [setStagePosition, setStageScale, shapes, stageHeight, stagePositionRef, stageScaleRef, stageWidth]);

  const centerStageOnSelectedBox = useCallback((): void => {
    const stage = stageRef.current;

    if (!selectedBoxId || !stage || stageWidth <= 1 || stageHeight <= 1) {
      return;
    }

    if (selectedBoxFloorId && selectedFloor && selectedBoxFloorId !== selectedFloor) {
      return;
    }

    const matches = stage.find((node: Konva.Node) => node.attrs?.id === selectedBoxId);
    const target =
      (matches.find((node) => node.getType() === 'Group') as Konva.Group | undefined) ?? (matches[0] as Konva.Node | undefined);

    if (!target) {
      return;
    }

    const rectSource = getBookingMapCenteringSourceNode(target) ?? target;
    const rect = rectSource.getClientRect({ relativeTo: stage });
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    const scale = stageScaleRef.current;

    const nextX = stageWidth / 2 - cx * scale;
    const nextY = stageHeight / 2 - cy * scale;

    animateStagePositionTo(nextX, nextY);
  }, [animateStagePositionTo, selectedBoxFloorId, selectedBoxId, selectedFloor, stageHeight, stageScaleRef, stageWidth]);

  // Fit on first valid measurement of a floor *with shapes loaded*; subsequent dimension
  // changes (rotation, keyboard show/hide) keep the user's current pan/zoom intact.
  useLayoutEffect(() => {
    if (!isOpen || stageWidth <= 1 || stageHeight <= 1 || !shapesConfigKey) {
      return;
    }

    if (shapes.length === 0) {
      // Floor data still loading — wait for it before fitting, otherwise we'd record this
      // floor as "fitted" with an empty bounds and never re-fit once shapes arrive.
      return;
    }

    if (lastFittedKeyRef.current === shapesConfigKey) {
      return;
    }

    fitToContent();
    lastFittedKeyRef.current = shapesConfigKey;
  }, [fitToContent, isOpen, shapes.length, shapesConfigKey, stageHeight, stageWidth]);

  // Center on selected box after fit. Using rAF means this fires after fitToContent's
  // synchronous state + ref writes have been flushed, giving Option A: fit → pan to box.
  // Omit canvasDimensions from deps so viewport-only resizes do not re-center (desktop map matches).
  useLayoutEffect(() => {
    if (!isOpen || !selectedBoxId) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      centerStageOnSelectedBox();
    });

    return (): void => {
      window.cancelAnimationFrame(frameId);
    };
  }, [centerStageOnSelectedBox, image, isOpen, selectedBoxId, selectedFloor, shapesConfigKey]);

  // Reset the fit tracking when the modal closes so reopening re-fits.
  useEffect(() => {
    if (!isOpen) {
      lastFittedKeyRef.current = null;
      setIsPreviewOpen(false);
    }
  }, [isOpen]);

  // Prevent the page behind the drawer from scrolling (iOS rubber-banding).
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;

    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';

    return (): void => {
      body.style.overflow = prevOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, [isOpen]);

  // Show bottom sheet when a bookable box on the current floor is selected.
  useEffect(() => {
    const matchesFloor = selectedBoxFloorId && selectedFloor ? selectedBoxFloorId === selectedFloor : true;
    setIsPreviewOpen(Boolean(selectedBox && matchesFloor));
  }, [selectedBox, selectedBoxFloorId, selectedFloor]);

  const handleStageDragEnd = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      clearPanTween();
      setStagePosition({ x: e.target.x(), y: e.target.y() });
    },
    [clearPanTween, setStagePosition],
  );

  const closeBoxPreview = useCallback((): void => {
    setIsPreviewOpen(false);
  }, []);

  const handleStageEmptyPointer = useCallback(
    (e: KonvaEventObject<Event>): void => {
      if (e.target === e.target.getStage()) {
        closeBoxPreview();
      }
    },
    [closeBoxPreview],
  );

  // Whether the Stage is currently mounted. The Stage only renders once dimensions have
  // been measured, so this state transition (false → true) is a reliable signal for the
  // touch-gestures hook to re-bind to the new Konva.Stage instance. We deliberately
  // avoid `setState` inside the Stage's ref callback — react-konva's StageWrap can
  // re-invoke ref callbacks on each commit, which would cause an infinite render loop.
  const isStageReady = hasFloorMap && canvasDimensions.width > 1 && canvasDimensions.height > 1;

  const assignStageRef = useCallback((node: Konva.Stage | null): void => {
    stageRef.current = node;
    node?.dragDistance(12);
  }, []);

  useStageTouchGestures({
    stageRef,
    scaleRef: stageScaleRef,
    positionRef: stagePositionRef,
    minScale: MOBILE_MIN_ZOOM,
    maxScale: MOBILE_MAX_ZOOM,
    setScale: setStageScale,
    setPosition: setStagePosition,
    cancelExternalAnimation: clearPanTween,
    enabled: isOpen && isStageReady,
    rebindTrigger: isStageReady,
  });

  const handleSelectFloor = useCallback(
    (floorId: string): void => {
      clearPanTween();
      // User picked a floor manually; if the selection lived on a different floor, drop
      // it so the sync effect doesn't immediately revert to the box's floor.
      if (selectedBoxFloorId && floorId !== selectedBoxFloorId) {
        onSelectBoxProp(undefined);
      }
      setSelectedFloor(floorId);
    },
    [clearPanTween, onSelectBoxProp, selectedBoxFloorId, setSelectedFloor],
  );

  return (
    <AntdDrawer
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      // 100dvh tracks the dynamic visible viewport (excluding mobile browser chrome).
      // 100vh on iOS Safari is the largest possible viewport, so the drawer ends up
      // taller than what's actually visible — the top bar gets hidden behind the URL
      // bar and the bottom sheet behind the toolbar.
      height="100dvh"
      closable={false}
      destroyOnClose
      styles={{
        body: {
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          maxHeight: '100%',
          overscrollBehavior: 'none',
        },
        wrapper: { boxShadow: 'none', height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' },
        content: { height: '100dvh', maxHeight: '100dvh', overflow: 'hidden' },
        mask: { touchAction: 'none' },
      }}
      className="warehouse-map-mobile-drawer"
    >
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-secondaryLight">
        {/* Top bar */}
        <div
          className="flex shrink-0 items-center justify-between gap-2 border-b border-secondaryAccent bg-white px-3"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)', paddingBottom: 8 }}
        >
          <button
            type="button"
            aria-label={t('Close')}
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent p-0"
          >
            <CloseIcon className="h-5 w-5 [&_path]:stroke-primary" />
          </button>
          <div className="min-w-0 flex-1 truncate text-center text-base font-semibold text-primary">
            {warehouseInfo?.name ?? t('Warehouse map')}
          </div>
          <button
            type="button"
            aria-label={t('Switch to list')}
            onClick={onSwitchToList}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent p-0 text-primary"
          >
            <UnorderedListOutlined style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Floor chips */}
        {floors && floors.length > 0 ? (
          <div className="shrink-0 border-b border-secondaryAccent bg-white px-4 py-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-primaryLight">{t('Choose floor')}</p>
            <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {floors.map((floor) => (
                <button
                  type="button"
                  key={floor.floorId}
                  onClick={() => {
                    handleSelectFloor(floor.floorId);
                  }}
                  className={classNames(
                    'shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors',
                    selectedFloor === floor.floorId ? 'border-accent bg-accent text-white' : 'border-secondaryAccent bg-white text-primary',
                  )}
                >
                  {getLocalizedString(floor.name)}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="shrink-0 border-b border-secondaryAccent bg-white px-4 py-2.5">
          <div className="flex gap-3 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {legendItems.map(({ key, label }) => (
              <div key={key} className="flex shrink-0 items-center gap-1.5 text-xs text-primary">
                <div className={classNames('h-3 w-3 shrink-0 rounded-full border', LEGEND_COLOR_CLASSES[key])} />
                <span className="whitespace-nowrap">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Discount banner */}
        {selectedBox ? (
          <div className="shrink-0">
            <BookingMapDiscountBanner selectedBox={selectedBox} rentOptions={rentOptions} />
          </div>
        ) : null}

        {/* Stage area */}
        <div ref={setStageParentRef} className="relative min-h-0 flex-1 overflow-hidden bg-[#F3F6F9]">
          {hasFloorMap && stageWidth > 1 && stageHeight > 1 ? (
            <Stage
              ref={assignStageRef}
              width={stageWidth}
              height={stageHeight}
              x={stagePosition.x}
              y={stagePosition.y}
              scaleX={stageScale}
              scaleY={stageScale}
              draggable
              onDragEnd={handleStageDragEnd}
              onClick={handleStageEmptyPointer}
              onTap={handleStageEmptyPointer}
            >
              <Layer>
                <KonvaImage
                  name="background-image"
                  image={image}
                  x={imageTransform.x}
                  y={imageTransform.y}
                  rotation={imageTransform.rotation}
                  opacity={mobileBackgroundOpacity}
                  listening={false}
                />
                <BookingMapShapes
                  shapes={shapes}
                  boxes={boxes}
                  selectedBoxId={selectedBoxId}
                  selectedFilter={selectedFilter}
                  allowedSizeCodeId={allowedSizeCodeId}
                  highlightOnlySelected={false}
                  onSelectBox={onSelectBoxProp}
                  onPickBox={selectBox}
                  notifyUnpickableTap={notifyBookingMapUnpickableTap}
                  applyStageCursor={applyStageCursor}
                  fontSize={labelFontSize}
                />
              </Layer>
            </Stage>
          ) : hasFloorMap ? null : (
            <div className="flex h-full items-center justify-center px-6 text-center text-base text-primaryLight">
              {t('Currently no map is available for this floor, choose another floor or switch to list mode')}
            </div>
          )}

          {hasFloorMap ? (
            <>
              {/* Floating zoom controls */}
              <div
                className={classNames(
                  'pointer-events-none absolute right-3 z-10 flex flex-col overflow-hidden rounded-lg border border-secondaryAccent bg-white shadow-md',
                  selectedBox ? 'top-1/2 -translate-y-1/2' : '',
                )}
                style={selectedBox ? undefined : { bottom: mapToolbarBottom }}
              >
                <button
                  type="button"
                  aria-label={t('Zoom in')}
                  disabled={!canZoomIn}
                  onClick={zoomIn}
                  className="pointer-events-auto flex h-10 w-10 min-w-[2.5rem] items-center justify-center border-0 border-b border-secondaryAccent bg-transparent p-0 text-primaryLight disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PlusOutlined className="text-base" />
                </button>
                <button
                  type="button"
                  aria-label={t('Zoom out')}
                  disabled={!canZoomOut}
                  onClick={zoomOut}
                  className="pointer-events-auto flex h-10 w-10 min-w-[2.5rem] items-center justify-center border-0 bg-transparent p-0 text-primaryLight disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <MinusOutlined className="text-base" />
                </button>
              </div>

              {!selectedBox ? (
                <div
                  className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-end border-t border-secondaryAccent bg-white/95 px-4 py-2 backdrop-blur-sm"
                  style={{ paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))' }}
                >
                  <button
                    type="button"
                    aria-label={t('Fit map to screen')}
                    onClick={fitToContent}
                    className="flex h-9 items-center justify-center rounded-full border border-secondaryAccent bg-white px-4 text-sm font-medium text-primary shadow-sm"
                  >
                    {t('Fit')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  aria-label={t('Fit map to screen')}
                  onClick={fitToContent}
                  className="absolute bottom-3 right-3 z-10 flex h-9 items-center justify-center rounded-full border border-secondaryAccent bg-white px-4 text-sm font-medium text-primary shadow-sm"
                  style={{ bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }}
                >
                  {t('Fit')}
                </button>
              )}
            </>
          ) : null}
        </div>

        {/* Bottom selected-box sheet */}
        {selectedBox && warehouseInfo ? (
          <div
            className="shrink-0 border-t border-secondaryAccent bg-white px-4 pt-3"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          >
            {isPreviewOpen ? (
              <div className="mb-3">
                <BookingMapBoxPreviewCard
                  box={selectedBox}
                  warehouseInfo={warehouseInfo}
                  accountType={accountType}
                  isShowPromotionPriceNeeded={isShowPromotionPriceNeeded}
                  onClose={closeBoxPreview}
                />
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              <BookingMapPriceBlock selectedBox={selectedBox} />
              {isSelectedBoxBookable ? (
                <Button containerClassName="shrink-0" onClick={handleBookBox}>
                  {t('Book now')}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </AntdDrawer>
  );
});

WarehouseMapMobile.displayName = 'WarehouseMapMobile';
